import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredUTM, utmToParams } from '../../../hooks/useUTM';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

const contactInfo = [
  { icon: 'ri-map-pin-2-line', label: 'Adresa', value: 'Ruská Nová Ves 16, 080 05 Prešov' },
  { icon: 'ri-phone-line', label: 'Telefón / WhatsApp', value: '+421 905 929 167' },
  { icon: 'ri-mail-line', label: 'Email', value: 'info@boostio.sk' },
  { icon: 'ri-time-line', label: 'Pracovná doba', value: 'Po–Pia, 9:00–18:00' },
];

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

export default function KontaktSection() {
  const { ref, inView } = useInView();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', gdpr: false });
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      if (name === 'message') setCharCount(value.length);
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.gdpr) return;
    if (charCount > 500) return;
    setStatus('sending');
    try {
      const utm = getStoredUTM();
      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        ...utmToParams(utm),
      });
      const res = await fetch('https://readdy.ai/api/form/d735c24lqq77n94m7c40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        navigate('/dakujeme');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="kontakt" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-chat-3-line text-orange-500 text-sm" />
            <span className="text-orange-600 text-sm font-semibold">Kontakt</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-zinc-900 mb-4 leading-tight">
            Začnime{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
              spolupracovať
            </span>
          </h2>
          <p className="text-zinc-500 text-lg max-w-xl mx-auto">
            Napíšte nám a do 24 hodín dostanete odpoveď spolu s bezplatnou analýzou vašej online prítomnosti.
          </p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-5 gap-8 transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Left panel */}
          <div className="lg:col-span-2 rounded-2xl overflow-hidden relative bg-zinc-950 flex flex-col">
            {/* Background image */}
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20dark%20office%20interior%20with%20large%20windows%20city%20view%20at%20night%2C%20professional%20business%20meeting%20room%20with%20orange%20accent%20lighting%2C%20contemporary%20workspace%20design%2C%20warm%20atmospheric%20glow%2C%20high%20quality%20photography&width=700&height=900&seq=contact1&orientation=portrait"
                alt="kancelária"
                className="w-full h-full object-cover object-center opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-900/80" />
            </div>

            {/* Content */}
            <div className="relative z-10 p-8 flex flex-col h-full min-h-[480px]">
              {/* Logo */}
              <div className="flex items-center gap-2 mb-auto">
                <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-lg">
                  <i className="ri-rocket-2-fill text-white text-sm" />
                </div>
                <span className="text-white font-bold text-lg">
                  boost<span className="text-orange-500">io</span>
                </span>
              </div>

              <div className="mt-16 mb-8">
                <h3 className="text-white text-3xl font-black mb-3">
                  Váš web si zaslúži<br />
                  <span className="text-orange-400">lepšie výsledky.</span>
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Bezplatná konzultácia bez záväzkov. Zistíme, kde strácate zákazníkov a navrhneme riešenie.
                </p>
              </div>

              {/* Contact info */}
              <div className="space-y-4">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-zinc-800/60 rounded-lg flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-orange-400 text-sm`} />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs mb-0.5">{item.label}</p>
                      <p className="text-white text-sm font-medium">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form panel */}
          <div className="lg:col-span-3 bg-zinc-50 rounded-2xl p-8 md:p-10">
            {status === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-6">
                  <i className="ri-check-line text-emerald-600 text-3xl" />
                </div>
                <h3 className="text-zinc-900 text-2xl font-black mb-3">Správa odoslaná!</h3>
                <p className="text-zinc-500 text-base mb-8 max-w-sm">
                  Ďakujeme za váš záujem. Ozveme sa vám do 24 hodín s bezplatnou analýzou.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="text-sm text-orange-500 hover:text-orange-600 font-semibold cursor-pointer"
                >
                  Odoslať ďalšiu správu
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-zinc-900 text-2xl font-black mb-2">Napíšte nám</h3>
                <p className="text-zinc-500 text-sm mb-8">Všetky polia sú povinné</p>

                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-zinc-700 text-sm font-semibold mb-2 block">Meno a priezvisko</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Ján Novák"
                        className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-700 text-sm font-semibold mb-2 block">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="jan@firma.sk"
                        className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-zinc-700 text-sm font-semibold mb-2 block">Telefón</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+421 9xx xxx xxx"
                      className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-zinc-700 text-sm font-semibold">Vaša správa</label>
                      <span className={`text-xs ${charCount > 450 ? 'text-red-500' : 'text-zinc-400'}`}>
                        {charCount}/500
                      </span>
                    </div>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="Opíšte váš projekt alebo otázku..."
                      className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200 resize-none"
                    />
                  </div>

                  {/* GDPR */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative mt-0.5 flex-shrink-0">
                      <input
                        type="checkbox"
                        name="gdpr"
                        checked={form.gdpr}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all duration-200 ${form.gdpr ? 'bg-orange-500 border-orange-500' : 'bg-white border-zinc-300 group-hover:border-zinc-400'}`}>
                        {form.gdpr && <i className="ri-check-line text-white text-xs" />}
                      </div>
                    </div>
                    <p className="text-zinc-500 text-xs leading-relaxed">
                      Súhlasím so spracovaním osobných údajov v zmysle{' '}
                      <a href="/ochrana-sukromia" className="text-orange-500 hover:text-orange-600 underline">zásad ochrany osobných údajov</a>
                      {' '}Kamila Frajtka – Boostio.
                    </p>
                  </label>

                  {status === 'error' && (
                    <div className="bg-red-50 border border-red-100 text-red-600 text-sm px-4 py-3 rounded-xl">
                      Odosielanie zlyhalo. Skúste to prosím znova.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || !form.gdpr}
                    className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-200 disabled:text-zinc-400 text-white font-bold py-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    {status === 'sending' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Odosielam...
                      </>
                    ) : (
                      <>
                        Odoslať správu
                        <i className="ri-send-plane-line" />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
